<template>
  <Loading v-if="loading" size="100px" />
  <DashboardLayout v-else>
    <template v-slot:title>
      <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center">
        <h1 class="text-left mb-2 mb-md-0">
          {{
            $store.state.racesStore.selectedRace
              ? $store.state.racesStore.selectedRace.short_name
              : ""
          }}
        </h1>
        <div v-if="$store.state.racesStore.selectedRace" class="d-flex flex-wrap">
          <button
            class="btn btn-primary mr-2 mb-2 mb-md-0"
            @click="exportRaceResults"
          >
            Export Results
          </button>
          <button
            class="btn btn-primary mr-2 mb-2 mb-md-0"
            @click="exportRaceTrackers"
          >
            Export Trackers
          </button>
          <button
            class="btn btn-primary mb-2 mb-md-0"
            @click="exportRaceRiders"
          >
            Export Riders
          </button>
        </div>
      </div>
      <hr class="mt-2" />
    </template>

    <template v-slot:body>
      <SelectRace :races="availableRaces" @select-race="selectRace" />
      <div class="card-deck" v-if="!loading">
        <DashboardCard
          cardTitle="Riders"
          :numberOfItems="numberOfRiders"
          :itemStates="riderStates"
          :referenceStates="riderRefData"
        >
          <template slot="icon">
            <i class="fa fa-biking mr-1" />
          </template>
        </DashboardCard>

        <DashboardCard
          cardTitle="Trackers"
          :numberOfItems="numberOfTrackers"
          :itemStates="trackerStates"
          :referenceStates="trackerRefData"
        >
          <template slot="icon">
            <i class="fa fa-satellite mr-1" />
          </template>
        </DashboardCard>

        <DashboardCard
          cardTitle="Wallets"
          :numberOfItems="numberOfWallets"
          :customContent="true"
        >
          <template slot="icon">
            <i class="fa fa-wallet mr-1" />
          </template>
          <template slot="customContent">
            <div v-if="wallets && wallets.length > 0" class="wallet-list">
              <div 
                v-for="wallet in wallets" 
                :key="wallet.uuid" 
                class="wallet-item d-flex justify-content-between align-items-center mb-2"
              >
                <div class="wallet-info">
                  <span class="wallet-name">{{ wallet.name || 'Unnamed Wallet' }}</span>
                </div>
                <div class="wallet-actions d-flex align-items-center">
                  <span class="wallet-balance badge badge-primary mr-2">
                    £{{ (wallet.balance / 100).toFixed(2) }}
                  </span>
                  <button 
                    class="btn btn-sm btn-outline-primary"
                    @click="downloadWalletTransactions(wallet)"
                    title="Download transactions"
                  >
                    <i class="fa fa-download"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="text-muted">
              No wallets found for this race
            </div>
          </template>
        </DashboardCard>
      </div>
      <div class="card-deck mt-4" v-if="!loading">
        <DashboardCard
          cardTitle="Control Points"
          :customContent="true"
        >
          <template slot="icon">
            <i class="fa fa-flag-checkered mr-1" />
          </template>
          <template slot="customContent">
            <div
              v-if="controlPoints.length > 0"
              class="table-responsive"
            >
              <table class="table table-sm control-points-table">
                <thead>
                  <tr>
                    <th>{{ selectedRaceShortName }}</th>
                    <th
                      v-for="cp in controlPoints"
                      :key="`name-${cp.uuid}`"
                      class="text-center"
                    >
                      {{ cp.name }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Closing Time</th>
                    <td
                      v-for="cp in controlPoints"
                      :key="`closing-${cp.uuid}`"
                      class="text-center"
                    >
                      {{ formatClosingTime(cp.closing_time) }}
                    </td>
                  </tr>
                  <tr>
                    <th>Timezone</th>
                    <td
                      v-for="cp in controlPoints"
                      :key="`tz-${cp.uuid}`"
                      class="text-center"
                    >
                      {{ cp.timezone }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted">
              No control points configured for this race
            </div>
          </template>
        </DashboardCard>
      </div>
      <div class="card-deck mt-4" v-if="!loading">
        <DashboardCard
          cardTitle="Tracker Return Dates"
          :customContent="true"
        >
          <template slot="icon">
            <i class="fa fa-calendar-alt mr-1" />
          </template>
          <template slot="customContent">
            <div
              v-if="trackerReturnDates.length > 0"
              class="table-responsive"
            >
              <table class="table table-sm tracker-return-table">
                <thead>
                  <tr>
                    <th>{{ selectedRaceShortName }}</th>
                    <th
                      v-for="trd in trackerReturnDates"
                      :key="`range-${trd.id}`"
                      class="text-center"
                    >
                      <div>Start: {{ formatDate(trd.window_start) }}</div>
                      <div>End: {{ formatDate(trd.window_end) }}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>{{ depositLabel }}</th>
                    <td
                      v-for="trd in trackerReturnDates"
                      :key="`total-${trd.id}`"
                      class="text-center"
                    >
                      <div>-{{ penceToPounds(trd.total_returned_in_pence) }}</div>
                      <small class="text-muted">{{ trd.percentage_to_be_returned }}%</small>
                    </td>
                  </tr>
                  <tr>
                    <th>{{ walletOneLabel }}</th>
                    <td
                      v-for="trd in trackerReturnDates"
                      :key="`w1-${trd.id}`"
                      class="text-center"
                    >
                      <div>{{ penceToPounds(trd.return_wallet_one_share_in_pence) }}</div>
                      <small class="text-muted" v-if="trd.return_wallet_one_percentage != null">
                        {{ formatPercentage(trd.return_wallet_one_percentage) }}%
                      </small>
                    </td>
                  </tr>
                  <tr>
                    <th>{{ walletTwoLabel }}</th>
                    <td
                      v-for="trd in trackerReturnDates"
                      :key="`w2-${trd.id}`"
                      class="text-center"
                    >
                      <div>{{ penceToPounds(trd.return_wallet_two_share_in_pence) }}</div>
                      <small class="text-muted" v-if="trd.return_wallet_two_percentage != null">
                        {{ formatPercentage(trd.return_wallet_two_percentage) }}%
                      </small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-muted">
              No tracker return dates configured for this race
            </div>
          </template>
        </DashboardCard>
      </div>
    </template>
  </DashboardLayout>
</template>

<script>
import moment from "moment-timezone";
import Loading from "@/components/renderingComponents/Loading";
import DashboardLayout from "@/components/layoutComponents/DashboardLayout";
import DashboardCard from "@/components/renderingComponents/DashboardCard";
import SelectRace from "@/components/renderingComponents/SelectRace";
import { penceToPoundString } from "@/common-functions.js";
import { riderData } from "@/reference/RiderReferenceData.js";
import { trackerData } from "@/reference/TrackerReferenceData.js";

export default {
  name: "RacePage",
  components: {
    Loading,
    DashboardLayout,
    DashboardCard,
    SelectRace,
  },
  data() {
    return {
      loading: false,
      riderStates: Object(),
      trackerStates: Object(),
      numberOfTrackers: 0,
      numberOfRiders: 0,
      wallets: [],
      numberOfWallets: 0,
      trackerReturnDates: [],
      controlPoints: [],
    };
  },
  computed: {
    availableRaces() {
      return this.$store.state.racesStore.availableRaces;
    },
    trackerRefData() {
      return trackerData.trackerStates;
    },
    riderRefData() {
      return riderData.riderStates;
    },
    selectedRaceShortName() {
      return this.$store.state.racesStore.selectedRace
        ? this.$store.state.racesStore.selectedRace.short_name
        : "";
    },
    raceTimezone() {
      return (
        this.$store.state.racesStore.selectedRace?.race_timezone || "UTC"
      );
    },
    walletOneLabel() {
      const firstWithName = this.trackerReturnDates.find(
        (trd) => trd.return_wallet_one_name
      );
      return firstWithName ? firstWithName.return_wallet_one_name : "Wallet 1";
    },
    walletTwoLabel() {
      const firstWithName = this.trackerReturnDates.find(
        (trd) => trd.return_wallet_two_name
      );
      return firstWithName ? firstWithName.return_wallet_two_name : "Wallet 2";
    },
    depositLabel() {
      const first = this.trackerReturnDates[0];
      if (!first || first.deposit_in_pence == null) return "Deposit";
      return penceToPoundString(first.deposit_in_pence);
    },
  },
  methods: {
    selectRace(race) {
      this.$store.commit("racesStore/setSelectedRace", race);
    },
    setRidersTrackers(selectedRace) {
      this.selectedRace = selectedRace;
      this.riderStates = selectedRace.rider_states;
      this.trackerStates = selectedRace.tracker_states;
      this.numberOfRiders = selectedRace.total_riders;
      this.numberOfTrackers = selectedRace.total_trackers;
      this.wallets = selectedRace.wallets || [];
      this.numberOfWallets = this.wallets.length;
      this.trackerReturnDates = selectedRace.tracker_return_dates || [];
      this.controlPoints = selectedRace.control_points || [];
    },
    formatClosingTime(closingTime) {
      if (!closingTime) return "";
      return moment(closingTime).tz(this.raceTimezone).format("ddd Do MMM, HH:mm");
    },
    formatDate(datetime) {
      return moment(datetime).tz(this.raceTimezone).format("ddd Do MMM");
    },
    penceToPounds(amountInPence) {
      if (amountInPence == null) return "";
      return penceToPoundString(amountInPence);
    },
    formatPercentage(value) {
      const num = Number(value);
      return Number.isInteger(num) ? num : num.toFixed(1);
    },
    exportRaceResults() {
      if (this.$store.state.racesStore.selectedRace) {
        this.$store.dispatch("racesStore/exportResults", this.$store.state.racesStore.selectedRace.uuid)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${this.$store.state.racesStore.selectedRace.short_name}-results.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            console.error("Error exporting results:", error);
          });
      }
    },
    exportRaceTrackers() {
      if (this.$store.state.racesStore.selectedRace) {
        this.$store.dispatch("racesStore/exportTrackers", this.$store.state.racesStore.selectedRace.uuid)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${this.$store.state.racesStore.selectedRace.short_name}-trackers.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            console.error("Error exporting trackers:", error);
          });
      }
    },
    exportRaceRiders() {
      if (this.$store.state.racesStore.selectedRace) {
        this.$store.dispatch("ridersStore/exportRiders", this.$store.state.racesStore.selectedRace.uuid)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${this.$store.state.racesStore.selectedRace.short_name}-riders.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          })
          .catch(error => {
            console.error("Error exporting riders:", error);
          });
      }
    },
    downloadWalletTransactions(wallet) {
      this.$store.dispatch("racesStore/exportWalletTransactions", wallet.uuid)
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          
          // Generate filename with date and time
          const now = new Date();
          const dateStr = now.toLocaleDateString('en-GB').replace(/\//g, '-');
          const timeStr = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-');
          const walletName = wallet.name || `wallet-${wallet.uuid}`;
          const fileName = `${walletName}-transactions-${dateStr}_${timeStr}.csv`;
          
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(error => {
          console.error("Error exporting wallet transactions:", error);
        });
    },
  },
  created() {
    this.$store.dispatch("usersStore/getMe").then((user) => {
      this.$store.commit("usersStore/setMe", user);
      this.$store.commit("racesStore/setAvailableRaces", user.races);
      const previouslySelectedUuid = this.$store.state.racesStore.selectedRace?.uuid;
      const race =
        user.races.find((r) => r.uuid === previouslySelectedUuid) || user.races[0];
      this.$store.commit("racesStore/setSelectedRace", race);
      this.setRidersTrackers(race);
    });

    this.$watch(
      () => this.$store.state.racesStore.selectedRace,
      (selectedRace) => {
        this.setRidersTrackers(selectedRace);
      }
    );
  },
};
</script>

<style scoped>
.wallet-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.wallet-item:last-child {
  border-bottom: none;
}

.wallet-info {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
}

.wallet-name {
  font-weight: 500;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wallet-balance {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}

.wallet-actions {
  gap: 0.5rem;
  flex-shrink: 0;
}

.wallet-actions .btn {
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tracker-return-table th,
.tracker-return-table td {
  vertical-align: middle;
}

.tracker-return-table thead th {
  background-color: #f8f9fa;
}

.control-points-table th,
.control-points-table td {
  vertical-align: middle;
}

.control-points-table thead th {
  background-color: #f8f9fa;
}
</style>
